import { ConfirmationDialog, NewFormSection as FormSection, useDialogs, SettingsPage } from '@paintscout/ui'
import type { ValueType } from '@paintscout/ui'
import type { ContactLeadSource, LeadSource } from '@paintscout/util/builder'
import {
  copyLeadSource,
  createLeadSource,
  deleteLeadSource,
  deleteLeadSources,
  getLeadSources,
  reorderLeadSources
} from '@paintscout/util/builder'
import { Form, useFormikContext } from 'formik'
import type { OptionsDocument, RatesDocument } from 'paintscout'
import React, { useEffect, useState } from 'react'
import { OptionsTileList } from '../..'
import { EditLeadSourceDialog } from '../../dialogs'

export interface LeadSourcesProps {
  onDialogConfirm?: () => void
}

function LeadSources({ onDialogConfirm, ..._props }: LeadSourcesProps) {
  const { openDialog, dismissDialog } = useDialogs()
  const { values, setFieldValue } = useFormikContext<{
    options: OptionsDocument
    rates: RatesDocument
  }>()
  const [selected, setSelected] = React.useState<ValueType[]>([])
  const { options } = values
  const [items, setItems] = useState<LeadSource[]>(getLeadSources({ options, type: 'leadSources' }))

  useEffect(() => {
    setItems(getLeadSources({ options, type: 'leadSources' }))
  }, [options])

  function handleCreateItem(_: React.MouseEvent) {
    const item = createLeadSource<'leadSources'>()
    openDialog(EditLeadSourceDialog, {
      options,
      type: 'leadSources',
      item,
      isNew: true,
      onConfirm: async (updatedOptions: OptionsDocument) => {
        setFieldValue('options', updatedOptions)
        dismissDialog()
        if (onDialogConfirm) {
          onDialogConfirm()
        }
      },
      onCancel: () => {
        dismissDialog()
      }
    })
  }

  function handleDeleteItem(event: any, key: string) {
    openDialog(ConfirmationDialog, {
      message: (
        <>
          <p>This item will no longer be available.</p>
          <p>Existing assignments will not be affected.</p>
        </>
      ),
      onConfirm: (_ev: React.MouseEvent<HTMLElement>) => {
        dismissDialog()
        const updatedOptions = deleteLeadSource({
          source: items.find((item) => item.name === key),
          type: 'leadSources',
          options
        })
        setFieldValue('options', updatedOptions)
        if (onDialogConfirm) {
          onDialogConfirm()
        }
      },
      onCancel: (_ev: React.MouseEvent<HTMLElement>) => {
        dismissDialog()
      }
    })
  }

  function handleDeleteBulkItems(event: React.MouseEvent, keys: ValueType[]) {
    openDialog(ConfirmationDialog, {
      message: (
        <>
          <p>These items will no longer be available.</p>
          <p>Existing assignments will not be affected.</p>
          <p>
            Please note, <strong>this can not be undone</strong>
          </p>
        </>
      ),
      onConfirm: (_ev: React.MouseEvent<HTMLElement>) => {
        dismissDialog()
        const selectedSources = items.filter((i) => keys.includes(i.name))
        const updatedOptions = deleteLeadSources({
          sources: selectedSources,
          type: 'leadSources',
          options
        })

        setFieldValue('options', updatedOptions)
        setSelected([])
        if (onDialogConfirm) {
          onDialogConfirm()
        }
      },
      onCancel: (_ev: React.MouseEvent<HTMLElement>) => {
        dismissDialog()
      }
    })
  }

  function handleEditItem(event: React.MouseEvent, key: string) {
    const item = items.find((item) => item.name === key)

    openDialog(EditLeadSourceDialog, {
      options,
      type: 'leadSources',
      item,
      isNew: false,
      onConfirm: async (updatedOptions: OptionsDocument, isDirty?: boolean) => {
        dismissDialog()
        setFieldValue('options', updatedOptions)
        if (onDialogConfirm && isDirty) {
          onDialogConfirm()
        }
      },
      onCancel: () => {
        dismissDialog()
      }
    })
  }

  function handleReorder(contactSources: ContactLeadSource[]) {
    const updatedOptions = reorderLeadSources({
      sources: contactSources,
      type: 'leadSources',
      options
    })
    setFieldValue('options', updatedOptions)
  }

  function handleCopyItem(event: any, key: string) {
    const updatedOptions = copyLeadSource({ options, type: 'leadSources', key })
    setFieldValue('options', updatedOptions)
  }

  return (
    <SettingsPage
      title={'Lead Sources'}
      callout={{
        path: 'settings-sources',
        content: 'Add your list of lead sources so you can keep track of how your customers heard about your services.',
        learnMoreLink: 'https://help.paintscout.com/en/articles/12044215-lead-source-tracking'
      }}
    >
      <Form>
        <FormSection topPadded>
          <OptionsTileList
            title={''}
            items={items.map((item) => ({ ...item, key: item.name }))}
            createTitle={'Add New Lead Source'}
            showCreate={true}
            selected={selected}
            setSelected={setSelected}
            onDeleteBulkItems={handleDeleteBulkItems}
            onReorder={handleReorder}
            onEditItem={handleEditItem}
            onDeleteItem={handleDeleteItem}
            onCreateClick={handleCreateItem}
            onCopyItem={handleCopyItem}
          />
        </FormSection>
      </Form>
    </SettingsPage>
  )
}

export default LeadSources
